// ABOUTME: Card representing one relation in the warehouse History left list.
// ABOUTME: Shows relation name + aggregated stats (totals, open, last activity).

import { Box, ButtonBase, useTheme } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Text from "../../../../../components/theme/text/Text";
import { tokens } from "../../../../../theme";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import { localeSettings } from "../../../../../utils/locales";

export const RelationHistoryCard = ({ relation, onClick, selected, sx }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const language = useSelector((state) => state.settings.language);
    const dateLocale = localeSettings[language]?.dateLocale;

    const lastActivity = relation.lastTicketActivityAt
        ? formatDistanceToNow(new Date(relation.lastTicketActivityAt), { addSuffix: true, locale: dateLocale })
        : null;

    return (
        <ButtonBase
            onClick={onClick}
            TouchRippleProps={{
                style: {
                    color: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[300]
                }
            }}
            sx={{
                width: 1,
                pl: 4,
                pr: 3,
                py: 3,
                background: selected
                    ? (theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50])
                    : (theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]),
                borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                display: "flex",
                gap: 1.5,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                transition: 'background 0.2s',
                ...sx,
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    background: generateColorFromTitle(relation.name ?? "", theme.palette.mode, 90),
                    "&:after": {
                        content: "''",
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        borderRadius: 4,
                        zIndex: -1,
                        background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0]
                    }
                }}
            >
                <Box
                    sx={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        borderRadius: 2.5,
                        background: generateIconColorFromTitle(relation.name ?? "", theme.palette.mode),
                    }}
                />
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        width: 1,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5,
                        gap: 1,
                    }}
                >
                    <Text bold noBreak sx={{ minWidth: 0 }}>{relation.name}</Text>
                    {lastActivity && (
                        <Text light semibold sx={{ flexShrink: 0, fontSize: 14 }}>{lastActivity}</Text>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Text  sx={{ fontSize: 14 }}>
                        {relation.totalTickets ?? 0} {t('tickets')}
                    </Text>
                    {relation.openTickets > 0 && (
                        <Box
                            sx={{
                                height:24,
                                lineHeight:"24px",
                                borderRadius:3,
                                px:1,
                                background:  colors.orange[100],
                            }}
                        >
                            <Text semibold variant="orange">{relation.openTickets} {t('open')}</Text>
                        </Box>
                    )}
                </Box>
            </Box>
        </ButtonBase>
    );
};

export default RelationHistoryCard;
